import { ButtonStyleCheckbox, ButtonStyleCheckboxGroup, CheckboxGroupOption, PREFER_NOT_TO_SAY, usePreferNotToSay } from '@uplift/ui-v2';
import { useField } from 'formik';
import { Fragment, useCallback, useMemo } from 'react';
import { includes, isEqual, sortBy } from 'lodash';

export type PreferenceFormProps = {
  name: string;
  formOptions: CheckboxGroupOption[];
  hideLabel?: boolean;
  includePreferNotToSay?: boolean;
  label?: string;
};

export const PreferenceForm: React.VFC<PreferenceFormProps> = ({
  name,
  formOptions,
  hideLabel = false,
  includePreferNotToSay = false,
  label = '(Optional)',
}) => {

  const [ field, , helpers ] = useField<unknown[]>(name);

  const { value, setValue, options } = usePreferNotToSay({
    name,
    options: formOptions,
    includePreferNotToSay,
    value: field.value,
    setValue: helpers.setValue,
  });

  const optionsToMap = useMemo(
    () =>
      options.map(opt => {
        return typeof opt === 'string' ? { label: opt, value: opt } : opt;
      }),
    [ options ]
  );

  const onChange = useCallback(
    (checkboxValue: string, checked: boolean) => {
      let updatedValues;

      if (checkboxValue === PREFER_NOT_TO_SAY) {
        updatedValues = checked ? [ checkboxValue ] : [];
      } else {
        updatedValues = checked
          ? [ ...value.filter(v => v !== PREFER_NOT_TO_SAY), checkboxValue ]
          : value.filter(v => !isEqual(v, checkboxValue));
      }

      const sorted = sortBy(updatedValues, updatedValue => {
        return optionsToMap.findIndex(option => isEqual(option.value, updatedValue));
      });

      setValue(sorted, true);
      helpers.setTouched?.(true, false);
    },
    [ value, setValue, helpers, optionsToMap ]
  );

  return (
    <div>
      <ButtonStyleCheckboxGroup label={hideLabel ? undefined : label} name={name}>
        {optionsToMap.map(({ label, value: checkboxValue, disabled }, index) => (
          <Fragment key={index}>
            <ButtonStyleCheckbox
              label={label}
              value={checkboxValue}
              key={checkboxValue}
              onChange={checked => onChange(checkboxValue, checked)}
              disabled={disabled}
              checked={includes(value, checkboxValue)}
            />
          </Fragment>
        ))}
      </ButtonStyleCheckboxGroup>
    </div>
  );
};
